import { z } from 'zod';
/**
 * ehr#682 (PROGRESS_NOTE_10-20-V141 V123 / V124 / V133, gap #43): the Note settings footer's
 * Sign timeliness and Supervision reports. GET /api/v1/progress-notes/reports hands back one row
 * per signed progress note in range with dates, names and counts only (never note text); both
 * windows filter the rows (Search users or clients) and summarise them with the functions here,
 * so the server and the window always count the same way.
 */
/** V141 TL_RANGES: the range buttons, and how many days back each reaches (0: all time). */
export const NOTE_REPORT_RANGES = [
    ['30d', 'Last 30 days', 30],
    ['90d', 'Last 90 days', 90],
    ['6m', 'Last 6 months', 183],
    ['all', 'All time', 0],
];
export const noteReportQuerySchema = z.object({
    range: z.enum(['30d', '90d', '6m', 'all']).default('6m'),
});
/** When a range starts (null: all time). */
export function noteReportFrom(range, now = new Date()) {
    const days = NOTE_REPORT_RANGES.find((r) => r[0] === range)?.[2] ?? 0;
    return days ? new Date(now.getTime() - days * 86_400_000) : null;
}
/** Search users or clients (V141 tlRows): the author's or the client's name contains the text. */
export function filterReportRows(rows, q) {
    const t = q.trim().toLowerCase();
    if (!t)
        return rows;
    return rows.filter((r) => r.authorName.toLowerCase().includes(t) || r.clientName.toLowerCase().includes(t));
}
const pctOf = (part, whole) => whole ? Math.round((part * 100) / whole) : null;
const avgOf = (xs) => xs.length ? Math.round((xs.reduce((a, x) => a + x, 0) / xs.length) * 10) / 10 : null;
/** V141 tlHTML: percent on time, notes signed, signed late, average hours, and By user. */
export function timelinessSummary(rows) {
    const late = rows.filter((r) => r.late).length;
    const users = new Map();
    for (const r of rows)
        users.set(r.authorId, [...(users.get(r.authorId) ?? []), r]);
    const byUser = [...users.values()]
        .map((list) => {
        const l = list.filter((r) => r.late).length;
        return {
            authorId: list[0].authorId,
            name: list[0].authorName,
            signed: list.length,
            onTime: list.length - l,
            late: l,
            pct: pctOf(list.length - l, list.length) ?? 0,
        };
    })
        .sort((a, b) => b.pct - a.pct || b.signed - a.signed || a.name.localeCompare(b.name));
    return {
        signed: rows.length,
        late,
        pct: pctOf(rows.length - late, rows.length),
        avgHours: avgOf(rows.map((r) => r.hoursAfter)),
        byUser,
    };
}
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** V141 tlChart: percent on time by week (30 and 90 days) or by month, by the signature's day here. */
export function timelinessTrend(rows, weekly) {
    const b = new Map();
    for (const r of rows) {
        const d = new Date(r.signedAt);
        const at = weekly
            ? new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7))
            : new Date(d.getFullYear(), d.getMonth(), 1);
        const k = ymd(at);
        const cur = b.get(k) ?? { at, rows: [] };
        cur.rows.push(r);
        b.set(k, cur);
    }
    return [...b.entries()]
        .sort((x, y) => (x[0] < y[0] ? -1 : 1))
        .map(([key, { at, rows: list }]) => ({
        key,
        label: weekly
            ? `${MON[at.getMonth()]} ${at.getDate()}`
            : `${MON[at.getMonth()]} ${String(at.getFullYear()).slice(2)}`,
        pct: pctOf(list.filter((r) => !r.late).length, list.length) ?? 0,
        signed: list.length,
    }));
}
/** V141 svHTML (V133): percent supervised, notes signed, co-signed, By user; plus By supervisor. */
export function supervisionSummary(rows) {
    const hours = (list) => avgOf(list.map((r) => r.hoursToCosign).filter((h) => h !== null));
    const sent = rows.filter((r) => r.needsCosign);
    const done = sent.filter((r) => r.cosignedAt);
    const users = new Map();
    for (const r of rows)
        users.set(r.authorId, [...(users.get(r.authorId) ?? []), r]);
    const byUser = [...users.values()]
        .map((list) => {
        const s = list.filter((r) => r.needsCosign);
        const c = s.filter((r) => r.cosignedAt);
        return {
            authorId: list[0].authorId,
            name: list[0].authorName,
            signed: list.length,
            needsCosign: s.length,
            cosigned: c.length,
            pct: pctOf(c.length, s.length),
            avgHoursToCosign: hours(c),
        };
    })
        .sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1) || b.signed - a.signed || a.name.localeCompare(b.name));
    const sups = new Map();
    for (const r of done) {
        const k = r.cosignerName ?? 'Unknown';
        sups.set(k, [...(sups.get(k) ?? []), r]);
    }
    const bySupervisor = [...sups.entries()]
        .map(([name, list]) => ({ name, cosigned: list.length, avgHoursToCosign: hours(list) }))
        .sort((a, b) => b.cosigned - a.cosigned || a.name.localeCompare(b.name));
    return {
        signed: rows.length,
        needsCosign: sent.length,
        cosigned: done.length,
        waiting: sent.length - done.length,
        pct: pctOf(done.length, sent.length),
        avgHoursToCosign: hours(done),
        byUser,
        bySupervisor,
    };
}
//# sourceMappingURL=noteReports.js.map